import IMask, { Definitions, FactoryOpts, InputMask, Masked } from "imask";
import { JQueryInput } from "./types";
import { registerEvents } from "./skyward.ui.events";

type MaskProperties = {
    mask: Masked<string> | Masked<Number>;
    lazy?: boolean;
    placeholderChar?: string;
    definitions: Definitions;
    scale?: number;
    thousandsSeparator?: string; // any single char
    padFractionalZeros?: boolean;
    normalizeZeros?: boolean;
    radix?: string; // fractional delimiter
    mapToRadix?: Array<string>; // symbols to process as radix
    // additional number interval options (e.g.)
    min?: string;
    max?: string;
};

const classNames =
{
    skipMask: "js-skipMask",
    maskInitialized: "js-maskInitialized",
    masked: "masked"
};

const skyward = window.skyward;

const map = new WeakMap<HTMLInputElement, InputMask<MaskProperties>>();

export let applyYearMask = function (element: JQuery<HTMLElement>)
{
    const mask = map.get(element[0] as HTMLInputElement);
    let yearFieldValue = mask?.value.replace(/_/g, "");

    switch (yearFieldValue?.length)
    {
        case 1:
            yearFieldValue = "000" + yearFieldValue;
            break;
        case 2:
            yearFieldValue =
                parseInt(yearFieldValue) >= 60
                    ? "19" + yearFieldValue
                    : "20" + yearFieldValue;
            break;
        case 3:
            yearFieldValue = "0" + yearFieldValue;
            break;
        default:
            return;
    }

    setValue(element, yearFieldValue);
    element.trigger("change");
};

export let applyMaskOnContainer = function (container: JQuery<HTMLElement>)
{
    container.each(function ()
    {
        const element = $(this);
        const data = element.data();
        if (data.mask !== undefined || data.decimalMask !== undefined || data.yearMask !== undefined)
        {
            init(element);
        }
    });
}

export let addMask = function (element: JQuery<HTMLElement>, mask: string, placeholder?: string)
{
    if (!element.hasClass(classNames.maskInitialized))
    {
        init(element);
        return;
    }

    if (element.length === 0)
    {
        return;
    }

    switch (element.prop("type"))
    {
        case "text":
        case "tel":
            break;
        default:
            return;
    }

    const value = element.val();
    removeMask(element);
    const definitions: Definitions =
    {
        "9": /\d/ as FactoryOpts,
        "^": /[A-Z]/ as FactoryOpts,
    };
    const maskProperties: MaskProperties =
    {
        mask: mask as unknown as Masked<string>,
        lazy: true,
        placeholderChar: "_",
        definitions: definitions,
    };

    if (typeof placeholder === "string")
    {
        if (placeholder == "")
        {
            maskProperties.placeholderChar = " ";
        }
        else
        {
            maskProperties.placeholderChar = placeholder;
        }
    }

    const masked: InputMask<MaskProperties> = IMask(element[0], maskProperties);
    element.addClass(classNames.masked);

    // Chrome updates the text caret position an unspecified amount of time after firing the focus event. We have to wait
    // for this to happen, otherwise the position set by alignCursorFriendly() will be overwritten.
    const showPlaceholder = skyward.debounce(function ()
    {
        if (document.activeElement === element[0] && masked.el)
        {
            // masked.el will be undefined if the mask was destroyed
            masked.updateOptions({ lazy: false });
            masked.alignCursorFriendly();
        }
    }, 10);

    element.on("focus.placeholder", showPlaceholder);
    element.on("focusout.placeholder", function ()
    {
        masked.updateOptions({ lazy: true });
    });

    if (skyward.window.isSafari)
    {
        element.on('blur', checkForChange);
    }

    map.set(element[0] as HTMLInputElement, masked);

    setValue(element, value);

    if (element.data("yearMask"))
    {
        applyYearMask(element);
    }
};

export let addDecimalMask = function (element: JQuery<HTMLElement>, mask: string)
{
    if (!element.hasClass(classNames.maskInitialized))
    {
        init(element);
        return;
    }

    if (element.prop("type") != "text")
    {
        if (element.prop("type") == "number")
        {
            mask = mask.replace(',', '');
            if (mask.includes('-'))
            {
                element.prop("min", Number.parseFloat(mask));
                element.prop("max", Number.parseFloat(mask.replace('-', '')));
            }
            else
            {
                element.prop("min", 0);
                element.prop("max", Number.parseFloat(mask));
            }



            element.on('change', validateValue);
            element.on('keyup', validateValue);

        }
        return;
    }

    const value = element.val() ?? "";
    const definitions: Definitions =
    {
        "9": /\d/ as FactoryOpts,
        "^": /[A-Z]/ as FactoryOpts,
    };

    const maskProperties: MaskProperties =
    {
        mask: Number as unknown as Masked<Number>,
        padFractionalZeros: true,
        definitions: definitions,
    };

    removeMask(element);
    maskProperties.radix = ".";
    maskProperties.scale = 0;

    if (mask.includes("."))
    {
        const masks = mask.split(".");
        const precision = masks[1].length;
        maskProperties.scale = precision;
        maskProperties.mapToRadix = ["."];
        const hideFractionalZeros = element.data().hideFractionalZeros;
        if (hideFractionalZeros && hideFractionalZeros.toString().toLowerCase() === "true")
        {
           maskProperties.padFractionalZeros = false;
        }
    }

    if (mask.includes(","))
    {
        maskProperties.thousandsSeparator = ",";
    }

    let allowNegative = false;
    if (mask.includes("-"))
    {
        maskProperties.min = mask.replace(/,/g, "");
        allowNegative = true;
    }
    else
    {
        maskProperties.min = "0";
    }

    maskProperties.max = mask.replace(/,/g, "").replace("-", "");
    maskProperties.normalizeZeros = false;

    const masked: InputMask<MaskProperties> = IMask(element[0], maskProperties);

    map.set(element[0] as HTMLInputElement, masked);
    setValue(element, value);
    element.addClass(classNames.masked);

    if (value !== undefined || value !== null)
    {
        if (decimalDoesNotMatchMask(value, maskProperties, allowNegative))
        {
            const adjustedValue = removeTrailingZeros(value);
            if (decimalDoesNotMatchMask(adjustedValue, maskProperties, allowNegative))
            {
                removeMask(element);
                element.val(value);
                return;
            }
            else
            {
                setValue(element, adjustedValue);
            }

        }
    }

    if (skyward.window.isSafari)
    {
        element.on('blur', checkForChange);
    }

    element.prop("maxlength", mask.length);
};

export let getUnmaskedValue = function (element: JQuery)
{
    if (element.hasClass(classNames.skipMask) || !element.hasClass(classNames.masked))
    {
        return element.val();
    }

    const mask = map.get(element[0] as HTMLInputElement);

    // This is needed for integer values that want to keep the leading zeros as a part of the number
    if (mask && element.data().decimalMask)
    {
        const decimalMask = element.data().decimalMask.toString();
        if (!decimalMask.includes(".") && !decimalMask.includes(","))
        {
            return mask.displayValue;
        }
    }

    return mask?.unmaskedValue;
};

export let setValue = function (element: JQuery<HTMLElement>, value: string | number | string[] | undefined)
{
    const mask = map.get(element[0] as HTMLInputElement) as InputMask<MaskProperties>;
    if (mask !== undefined && value !== undefined)
    {
        mask.value = value !== null ? value.toString() : "";
        mask.updateControl();
    }
};

export let removeMask = function (element: JQuery<HTMLElement>)
{
    const htmlElement = element[0] as HTMLInputElement;
    if (map.has(htmlElement))
    {
        element.off("focus.placeholder");
        element.off("focusout.placeholder");
        element.off('blur', checkForChange);
        const mask = map.get(htmlElement) as InputMask<MaskProperties>;
        const unmaskedValue = mask?.unmaskedValue as string;
        map.delete(htmlElement);
        mask.destroy();
        element.removeClass(classNames.masked);
        element.val(unmaskedValue);
    }
};

const events =
{
    init: init,
    // This is used to load the script on a page without running any logic
    load: () => { }
};
registerEvents("datamask", events);

function init(selectorInput: JQueryInput)
{
    const element = $(selectorInput);
    const data = element.data();

    if (element.hasClass(classNames.maskInitialized))
    {
        return;
    }

    element.addClass(classNames.maskInitialized);

    if (data.mask !== undefined)
    {
        data.mask = data.mask.toString();

        if (data.skipMask != null)
        {
            skipMask(element);
        }
        else
        {
            addMask(element, data.mask, data.maskPlaceholder);
        }
    }
    else if (data.decimalMask !== undefined)
    {
        const mask = String(data.decimalMask);

        if (mask && !element.prop('readonly'))
        {
            const validMask = /^(-)?(?:9{1,3}(?:,9{3})*|9+)(?:\.9+)?$/;
            const maskParts = validMask.exec(mask);

            if (!maskParts)
            {
                throw `decimalMask: Invalid mask (${mask}), must be like ${validMask}`;
            }

            addDecimalMask(element, mask);
        }
        return;
    }

    if (data.yearMask !== undefined)
    {
        element.on("focusout", function ()
        {
            applyYearMask($(this));
        });
    }
}

function skipMask(element: JQuery)
{
    const data = element.data();
    const skipMask = element.getPrefix() + data.skipMask;

    const forElement = $("#" + skipMask.replace(/\./g, ""));

    if (forElement.length === 0)
    {
        return;
    }

    const isChecked = forElement.is(":checked");
    toggleSkipMask(element, isChecked, data.mask, data.maskPlaceholder);

    forElement.on("change", function ()
    {
        const isChecked = forElement.is(":checked");
        toggleSkipMask(element, isChecked, data.mask, data.maskPlaceholder);
    });
}

function toggleSkipMask(element: JQuery, skipMask: boolean, mask: string, maskPlaceholder: string)
{
    element.toggleClass(classNames.skipMask, skipMask);

    if (skipMask)
    {
        removeMask(element);
    }
    else
    {
        addMask(element, mask, maskPlaceholder);
    }
}

function validateValue(event: JQuery.TriggeredEvent)
{
    const element = $(event.target);
    const value = parseFloat(element.val()?.toString() ?? '');
    const min = parseFloat(element.prop('min'));
    const max = parseFloat(element.prop('max'));

    if (value < min)
    {
        element.val(min);
    } else if (value > max)
    {
        element.val(max);
    }
}

function decimalDoesNotMatchMask(value: number | string | string[], maskProperties: MaskProperties, allowNegative: boolean): boolean
{
    const currentValue = value.toString();
    const valueParts = currentValue.split(".");
    const maxParts = maskProperties.max?.split(".") ?? "";

    const hasInvalidNegativeSign = !allowNegative && currentValue.includes("-");
    const valueExceedsMaskBeforeRadix = valueParts[0].replace(/-/g, "").length > maxParts[0].length;
    const valueScaleExceedsMaskScale = valueParts.length > 1 && valueParts[1].length > (maskProperties !== undefined ? maskProperties.scale ?? 0 : 0);
    const valueHasRadixPointWithZeroScale = valueParts.length > 1 && valueParts[1].length === 0 && maskProperties.scale === 0;

    return hasInvalidNegativeSign || valueExceedsMaskBeforeRadix || valueScaleExceedsMaskScale || valueHasRadixPointWithZeroScale;
}

function removeTrailingZeros(value: number | string | string[]): number
{
    const valueString = value.toString();
    const trimmedValue = valueString.replace(/(\.[0-9]*[1-9])0+$/, "$1");

    return parseFloat(trimmedValue);
}

function checkForChange(event: JQuery.TriggeredEvent)
{
    const element = $(event.target);
    const value = element?.val()?.toString();
    const originalValue = element.data().originalValue.toString();
    if (value !== undefined && value != originalValue)
    {
        element.trigger("change");
    }
}